import React from 'react';
import { Card, Divider, Form, Input, Space } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Address } from '../../components/Address';
import { InfoLine } from '../../order/components/InfoLine';
import { CustomerSelect } from '../../features/customer/components/customer-select';

import { Order, Customer } from '../../hooks/data/models';

interface CustomerDataProps {
  customer: any;
  shippingAddress: any;
  billingAddress: any;
  shippingAddressTitle?: string;
  billingAddressTitle?: string;
  margin?: boolean;
  isEditMode?: boolean;
  onDataEdited?: (updates: Partial<Order>) => void
}

export const CustomerData: React.FC<CustomerDataProps> = ({
  customer,
  shippingAddress,
  billingAddress,
  shippingAddressTitle,
  billingAddressTitle,
  onDataEdited,
  margin = false,
  isEditMode = false
}) => {

  const handleCustomerEdit = (value: Customer) => {
    if(!onDataEdited)
      return;
    onDataEdited({customerId: value._id});
  }
  
  const handleShippingAddressEdit = (update: object) => {
    if(!onDataEdited)
      return;
    const addressCopy = {...shippingAddress,
      ...update
    };
    onDataEdited({shippingAddress: addressCopy});
  }

  const handleBillingAddressEdit = (update: object) => {
    if(!onDataEdited)
      return;
    const addressCopy = {...billingAddress,
      ...update
    };
    onDataEdited({billingAddress: addressCopy});
  }

  return (
    <Card title="Customer" loading={!customer} style={{marginTop: margin ? '1.6rem' : ''}}>

      <CardSubtitle>Contact information</CardSubtitle>

      {customer && (
        <Container>
          <div style={{ fontSize: '2rem', color: '#96a9c8' }}><UserOutlined /></div>
          <CustomerName style={{marginLeft: '1.6rem'}}>
            <a href={`/customers/${customer._id}`}>{customer?.firstName} {customer?.lastName}</a>
          </CustomerName>
        </Container>
      )}
      { customer && <InfoLine icon={<MailOutlined />} title={customer.email || ''} /> }
      { customer && <InfoLine icon={<PhoneOutlined />} title={customer.phone?.number || ''} /> }

      {customer && isEditMode && (
        <Form.Item>
          <Divider orientation='left'>Update Customer</Divider>
          <CustomerSelect onChange={handleCustomerEdit}/>
        </Form.Item>
      )}

      {shippingAddress && (
        <>
          <SectionDivider />
          <CardSubtitle>{shippingAddressTitle ?? "Shipping address"}</CardSubtitle>
          {!isEditMode && (
          <Address address={shippingAddress} />
          )}
          {isEditMode && (
            <>
              <Input value={shippingAddress.line1} onChange={(e) => handleShippingAddressEdit({line1: e.target.value})} placeholder='line1'/>
              <Input value={shippingAddress.line2} onChange={(e) => handleShippingAddressEdit({line2: e.target.value})} placeholder='line2'/>
              <Space.Compact>
                <Input value={shippingAddress.postalCode} onChange={(e) => handleShippingAddressEdit({postalCode: e.target.value})} placeholder='postal code' style={{ width: '20%' }}/>
                <Input value={shippingAddress.city} onChange={(e) => handleShippingAddressEdit({city: e.target.value})} placeholder='city' style={{ width: '80%' }}/>
              </Space.Compact>
              <Input value={shippingAddress.state} onChange={(e) => handleShippingAddressEdit({state: e.target.value})} placeholder='state'/>
              <Input value={shippingAddress.country} onChange={(e) => handleShippingAddressEdit({country: e.target.value})} placeholder='country'/>
            </>
          )}
        </>
      )}
      {billingAddress && (
        <>
          <SectionDivider />
          <CardSubtitle>{billingAddressTitle ?? "Billing address"}</CardSubtitle>
          {!isEditMode && (
          <Address address={billingAddress} />
          )}
          {isEditMode && (
            <>
              <Input value={billingAddress.line1} onChange={(e) => handleBillingAddressEdit({line1: e.target.value})} placeholder='line1'/>
              <Input value={billingAddress.line2} onChange={(e) => handleBillingAddressEdit({line2: e.target.value})} placeholder='line2'/>
              <Space.Compact>
                <Input value={billingAddress.postalCode} onChange={(e) => handleBillingAddressEdit({postalCode: e.target.value})} placeholder='postal code' style={{ width: '20%' }}/>
                <Input value={billingAddress.city} onChange={(e) => handleBillingAddressEdit({city: e.target.value})} placeholder='city' style={{ width: '80%' }}/>
              </Space.Compact>
              <Input value={billingAddress.state} onChange={(e) => handleBillingAddressEdit({state: e.target.value})} placeholder='state'/>
              <Input value={billingAddress.country} onChange={(e) => handleBillingAddressEdit({country: e.target.value})} placeholder='country'/>
            </>
          )}
        </>
      )}
    </Card>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 0.8rem;
  }
`;

const CardSubtitle = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  font-family: Quicksand, 'sans-serif';
  color: #3e475e;
  padding-bottom: 1.6rem;
`;

const CustomerName = styled.div`
  font-size: 1.6rem;
  font-family: Quicksand, 'sans-serif';
  font-weight: 500;
  color: #3e475e;
`;

const SectionDivider = styled.div`
  height: 1px;
  margin: 1.6rem 0;
  background-color: #f0f5f9;
`;
